// NodeAnnualDateInput.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  ANNUAL_DATE_MONTH_OPTIONS,
  buildAnnualDateValue,
  getAnnualDateOptionDayCount,
  normalizeAnnualDateValue,
} from 'utilities/AnnualDate';

// CSS
import './NodeAnnualDateInput.css';

// Components
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * NodeAnnualDateInput
 *
 * Renders an annual-date input using month/day selects plus an optional year.
 *
 * @param {object|null} value - Current annual-date field value.
 * @param {Function} onChange - Callback when the value changes.
 * @param {boolean} hybrid - Whether to use hybrid input styling.
 *
 * @returns {ReactNode} The rendered annual-date input.
 */
const NodeAnnualDateInput = ({ value, onChange, hybrid = true }) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [monthValue, setMonthValue] = useState('');
  const [dayValue, setDayValue] = useState('');
  const [yearValue, setYearValue] = useState('');

  // ----- MEMOS ----- //

  const normalizedValue = useMemo(() => normalizeAnnualDateValue(value), [value]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    setMonthValue(normalizedValue?.month ? String(normalizedValue.month) : '');
    setDayValue(normalizedValue?.day ? String(normalizedValue.day) : '');
    setYearValue(normalizedValue?.year ? String(normalizedValue.year) : '');
  }, [normalizedValue]);

  // ----- MEMOS ----- //

  const dayOptions = useMemo(() => {
    const count = getAnnualDateOptionDayCount(monthValue);
    return Array.from({ length: count }, (_, index) => {
      const numericDay = index + 1;
      return {
        value: String(numericDay),
        label: String(numericDay),
      };
    });
  }, [monthValue]);

  // ----- UTILITIES ----- //

  const getCommittedYearValue = () => (
    normalizedValue?.year ? String(normalizedValue.year) : ''
  );

  const emitChange = (nextMonth, nextDay, nextYear) => {
    if (typeof onChange !== 'function') return;

    const nextValue = buildAnnualDateValue({
      month: nextMonth,
      day: nextDay,
      year: nextYear,
    });

    onChange(nextValue);
  };

  // ----- HANDLERS ----- //

  /**
 * Handles month change.
 *
 * @param {*} event
 */

  const handleMonthChange = (event) => {
    const nextMonth = event.target.value;
    const maxDay = getAnnualDateOptionDayCount(nextMonth);
    const nextDay = Number(dayValue) > maxDay ? '' : dayValue;

    setMonthValue(nextMonth);
    setDayValue(nextDay);
    emitChange(nextMonth, nextDay, yearValue);
  };

  /**
 * Handles day change.
 *
 * @param {*} event
 */
  const handleDayChange = (event) => {
    const nextDay = event.target.value;
    setDayValue(nextDay);
    emitChange(monthValue, nextDay, yearValue);
  };

  /**
 * Handles year change.
 *
 * @param {*} event
 */
  const handleYearChange = (event) => {
    const sanitizedYear = String(event.target.value || '')
      .replace(/\D/g, '')
      .slice(0, 4);

    setYearValue(sanitizedYear);

    if (sanitizedYear === '' || sanitizedYear.length === 4) {
      emitChange(monthValue, dayValue, sanitizedYear);
    }
  };

  /**
 * Handles year blur.
 */
  const handleYearBlur = () => {
    const committedYearValue = getCommittedYearValue();

    if (yearValue === '') {
      if (committedYearValue !== '') {
        emitChange(monthValue, dayValue, '');
      }
      return;
    }

    if (yearValue.length === 4) {
      if (yearValue !== committedYearValue) {
        emitChange(monthValue, dayValue, yearValue);
      }
      return;
    }

    setYearValue(committedYearValue);
  };

  // ----- RENDER ----- //

  return (
    <div className="node-annual-date-input-container">

      {/* FIELD MONTH */}
      <div className="node-annual-date-input-field-month">
        <SelectInput
          value={monthValue}
          options={ANNUAL_DATE_MONTH_OPTIONS}
          placeholder="Month"
          hybrid={hybrid}
          onChange={handleMonthChange}
        />
      </div>

      {/* FIELD DAY */}
      <div className="node-annual-date-input-field-day">
        <SelectInput
          value={dayValue}
          options={dayOptions}
          placeholder="Day"
          disabled={!monthValue}
          hybrid={hybrid}
          onChange={handleDayChange}
        />
      </div>

      {/* FIELD YEAR */}
      <div className="node-annual-date-input-field-year">
        <TextInput
          value={yearValue}
          placeholder="Year (optional)"
          inputMode="numeric"
          maxLength={4}
          startIconColor={theme.foregroundMuted}
          iconSize={20}
          hybrid={hybrid}
          onBlur={handleYearBlur}
          onChange={handleYearChange}
        />
      </div>

    </div>
  );
};

// Dev Tools
NodeAnnualDateInput.displayName = 'NodeAnnualDateInput';

export default NodeAnnualDateInput;
